/**
 * Base CSS styles for the survey widget.
 * Includes the main survey container, topbar, progress bar, close/back buttons,
 * and utility classes. Uses BEM naming with ph- prefix and CSS custom properties.
 */
export const baseCSS = `
    /* ===== Global reset inside shadow DOM ===== */
    body {
        font-family: var(--ph-font-family);
        white-space: pre-line;
    }

    /* ===== Main survey container ===== */
    .ph-survey {
        display: flex;
        flex-direction: column;
        background-color: var(--ph-bg-color);
        padding: 16px;
        border-radius: 8px;
        font-family: var(--ph-font-family);
        line-height: normal;
        box-sizing: border-box;
        color: var(--ph-text-color);
    }

    /* ===== Internal box (question wrapper) ===== */
    .ph-survey__inner {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 12px;
        font-family: var(--ph-font-family);
        padding: 0;
        box-sizing: border-box;
        line-height: normal;
        flex: 1;
        min-height: 0;
    }

    .ph-question__body {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        gap: 12px;
        width: 100%;
    }

    /* ===== Top bar (progress + close) ===== */
    .ph-topbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        gap: 24px;
        margin-bottom: 16px;
    }

    .ph-topbar--close-only {
        justify-content: end;
    }

    /* ===== Progress bar container ===== */
    .ph-progress {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0;
        flex: none;
        flex-grow: 1;
    }

    /* Progress info column */
    .ph-progress__info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0px;
        gap: 4px;
        width: 100%;
        flex: none;
        order: 0;
        flex-grow: 1;
    }

    /* Progress label row */
    .ph-progress__label-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0px;
        gap: 10px;
        width: 100%;
        height: 13px;
        flex: none;
        order: 0;
        align-self: stretch;
        flex-grow: 0;
    }

    /* Progress label text */
    .ph-progress__label {
        font-family: var(--ph-font-family);
        font-style: normal;
        font-weight: 400;
        font-size: var(--ph-font-tiny);
        line-height: 13px;
        display: flex;
        align-items: center;
        color: var(--ph-text-color-50);
        flex: none;
        order: 0;
        flex-grow: 0;
    }

    /* Progress track container */
    .ph-progress__track {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0px;
        gap: 8px;
        width: 100%;
        height: 4px;
        background-color: var(--ph-text-color-16);
        border-radius: 100px;
        flex: none;
        order: 1;
        align-self: stretch;
        flex-grow: 0;
    }

    /* Progress bar fill */
    .ph-progress__fill {
        height: 4px;
        background: var(--ph-element-color);
        border-radius: 8px;
        flex: none;
        order: 0;
        flex-grow: 0;
    }

    /* ===== Close button ===== */
    .ph-topbar__close {
        background-color: transparent;
        border: none;
        padding: 0;
    }

    button.ph-topbar__close {
        background-color: transparent;
        border: none;
        color: var(--ph-text-color-75);
        font-size: var(--ph-font-small);
        padding: 0;
        cursor: pointer;
    }

    /* ===== Back button ===== */
    .ph-topbar__back {
        background-color: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        color: var(--ph-text-color-75);
    }

    .ph-topbar__close:focus-visible,
    .ph-topbar__back:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
        border-radius: 4px;
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            transition-duration: 0.01ms !important;
            animation-duration: 0.01ms !important;
        }
    }

    /* ===== Row utility ===== */
    .ph-row {
        margin-bottom: 16px;
    }

    /* ===== Hidden utility ===== */
    .ph-hidden {
        display: none !important;
    }
`;
